/**
 * 画布属性/功能说明文案。
 * key 优先匹配 `${component}.${attrKey}`，否则匹配 `attrKey` / `event.${key}` / `section.${name}`。
 * 「基本属性」Tab 与「位置属性」不展示帮助点；其余带橘黄点的项可点击查看。
 */
export const ATTR_HELP_MAP = {
  // —— 位置（一般不展示点，保留文案供兜底） ——
  left: {
    title: "X 坐标",
    desc: "组件相对画布左上角的水平位置（像素）。",
    usage: "可在属性中输入，或在画布上拖拽组件。",
    impact: "影响预览与导出工程中的布局位置。",
  },
  top: {
    title: "Y 坐标",
    desc: "组件相对画布左上角的垂直位置（像素）。",
    usage: "可在属性中输入，或在画布上拖拽组件。",
    impact: "影响预览与导出工程中的布局位置。",
  },
  width: {
    title: "宽度 W",
    desc: "组件显示宽度（像素）。",
    usage: "输入数值，或拖拽选中框左右边缘调整。",
    impact: "过小可能导致文字/图标显示不全。",
  },
  height: {
    title: "高度 H",
    desc: "组件显示高度（像素）。",
    usage: "输入数值，或拖拽选中框上下边缘调整。",
    impact: "过小可能导致文字/图标显示不全。",
  },

  // —— 通用外观 ——
  opacity: {
    title: "透明度",
    desc: "控制组件整体透明度。取值通常在 0～1 之间：1 完全不透明，0 完全透明（仍可能保留点击热区）。",
    usage: "在「组件属性」中调节数值；也可通过「组件联动」在预览时动态改透明度做显隐动画。",
    impact: "只影响观感；透明后若仍可点，注意热区不要误挡其它控件。",
  },
  borderRadius: {
    title: "圆角",
    desc: "按钮等组件四角的圆角半径（像素）。数值越大，角越圆；过大近似胶囊/圆形。",
    usage: "按 UI 稿填写；圆形按钮模板本身已带圆角策略，可再微调。",
    impact: "仅外观，不改变实际点击区域计算方式。",
  },
  fontSize: {
    title: "字体大小",
    desc: "文字字号。用于按钮名称、文本内容、滑块数值、可视化文件名等。",
    usage: "按画布分辨率选择合适字号；字号过大时请同步加大组件宽高。",
    impact: "过大易溢出裁切；过小在终端上不易阅读。",
  },
  color: {
    title: "字体颜色",
    desc: "文字前景色。",
    usage: "点选色板；注意与背景色对比度，保证可读。",
    impact: "仅外观。",
  },
  fontColor: {
    title: "字体颜色",
    desc: "滑块等组件上数值/标签的文字颜色。",
    usage: "与轨道、圆点颜色搭配，保证对比清晰。",
    impact: "仅外观。",
  },
  fontFamily: {
    title: "字体",
    desc: "工程内置字体包中的字体样式。",
    usage: "从下拉选择；特殊字体需确认终端客户端已内置对应资源。",
    impact: "终端缺少字体时可能回退为默认字体，版式会变化。",
  },
  backgroundColor: {
    title: "背景颜色",
    desc: "组件或页面的纯色背景。当背景类型为「颜色」时生效；若同时配置了背景图，通常以图片为准。",
    usage: "先设背景类型为颜色，再选色值。页面背景会影响整页底色。",
    impact: "影响视觉层次；深色背景上注意改浅色文字。",
  },
  backgroundType: {
    title: "背景类型",
    desc: "在「颜色背景」与「图片背景」之间切换，决定下方显示哪一组配置项。",
    usage: "选颜色 → 调背景色；选图片 → 从素材库上传/选择背景图。按钮还有单独的「按压背景」类型。",
    impact: "切换后另一类配置不会作为当前生效背景。",
  },
  backgroundImage: {
    title: "背景图",
    desc: "使用图片作为组件背景（按钮常态、状态灯、可视化等）。",
    usage: "点击上传区打开素材库选择；可删除恢复默认。建议用与组件宽高接近的图，减少拉伸变形。",
    impact: "图片会计入工程资源，增大导出包；过大图片可能影响终端加载。",
  },

  // —— 页面 Back ——
  pageName: {
    title: "页面名称",
    desc: "当前页面在左侧页树、跳转事件选页列表、以及终端菜单中显示的名称。",
    usage: "建议用业务语义命名（如「首页」「灯光控制」），避免「页面1、页面2」难以辨认。",
    impact: "改名后跳转配置里仍引用同一页面，显示文案会更新。",
  },
  pageWidth: {
    title: "子页面宽",
    desc: "仅子页面有效：子页面画布逻辑宽度，默认约 1200。主页面使用工程顶栏宽高。",
    usage: "按嵌入区域设计调整；主页上的「子页面」组件占位会随该尺寸变化。",
    impact: "过大会在主页显得拥挤；过小内部组件易挤在一起。",
  },
  pageHeight: {
    title: "子页面高",
    desc: "仅子页面有效：子页面画布逻辑高度，默认约 700。",
    usage: "与宽度一起规划子页版式；改完后到主页预览嵌入效果。",
    impact: "影响子页面组件在主页中的占位高度。",
  },
  grid: {
    title: "网格",
    desc: "编辑态是否在画布上显示网格辅助线，方便对齐。",
    usage: "需要精细对齐时打开；一般不影响终端最终画面。",
    impact: "仅编辑辅助，便于布局。",
  },
  backPicSrc: {
    title: "背景图片",
    desc: "页面级背景图，铺在当前页最底层。",
    usage: "从素材库选择；可与「图片尺寸」配合：铺满拉伸填框，等比例完整显示（可能露出背景色）。",
    impact: "计入工程资源与导出体积。",
  },
  isShowMenu: {
    title: "是否展示到菜单",
    desc: "控制该页是否出现在终端侧菜单/页面列表中，方便用户主动进入。",
    usage: "纯跳转中转页、弹层式子逻辑页可选「否」，避免菜单过长。",
    impact: "隐藏后仍可通过按钮「跳转事件」进入，只是菜单里找不到。",
  },
  bindPage: {
    title: "绑定子页面",
    desc: "「子页面」组件要嵌入显示哪一个已创建的子页内容。未绑定则只显示占位。",
    usage: "先在页树新增子页面并完成内部布局，再回到主页选中子页面组件，在此下拉选择目标子页。",
    impact: "预览/运行时渲染被绑定子页的全部组件；切换绑定会立刻换内容。子页面宽高由子页自身属性决定。",
  },

  // —— 按钮 ——
  inputContent: {
    title: "名称/内容",
    desc: "按钮上的显示文字，或文字组件的正文内容。",
    usage: "直接编辑；按钮还可另配「按压名称」在按下时换文案。文字组件可再开滚动、允许变更等。",
    impact: "预览与终端上直接可见，是用户识别功能的主要文案。",
  },
  btnPressName: {
    title: "按钮按压名称",
    desc: "按钮处于按压态时显示的文字，可与常态名称不同（例如「开启」/「关闭」）。",
    usage: "填写后按压态优先显示该文案；常与按压背景、联动状态一起用。",
    impact: "提升状态可读性；留空则按压态仍显示常态名称。",
  },
  alias: {
    title: "别名",
    desc: "按钮在工程内的逻辑标识，供定时、交互包、指令绑定等按名称引用。",
    usage: "开启「定时」等能力时务必填写，且全工程尽量唯一（如 light_on_1）。避免中文空格与特殊符号。",
    impact: "重复或为空可能导致校验失败、无法保存，或运行时找不到目标按钮。",
  },
  btnLoading: {
    title: "按钮 loading",
    desc: "点击后进入短暂加载态，期间通常不可再次触发，用于防连点、等设备反馈。",
    usage: "开启后设置「持续时间」秒数；适合发指令后需要等待的场景。",
    impact: "时间过短防不住连点；过长会让人感觉卡顿。",
  },
  btnLoadingTime: {
    title: "loading 持续时间",
    desc: "按钮 loading 持续的秒数。",
    usage: "配合「按钮 loading」开启；按指令往返时延估算（常见 0.5～2 秒）。",
    impact: "影响可再次点击的间隔。",
  },
  kqds: {
    title: "开启定时",
    desc: "是否启用与定时相关的按钮逻辑（与别名、后台定时策略配合）。",
    usage: "开启后请填写唯一「别名」，并确认工程/后台侧定时配置已指向该别名。",
    impact: "影响按钮是否参与定时触发；配置不全时可能校验不通过。",
  },
  oneClick: {
    title: "是否单击按钮",
    desc:
      "切换按钮工作模式：\n· 关闭（按压模式）：可配再次按压联动、两组指令等开关类交互；\n· 开启（单击模式）：可配跳转事件、点击绑定到其它组件事件。",
    usage: "纯跳转、打开视频/轮播等一次动作 → 开单击；需要按一下开、再按一下关 → 关单击并配指令2/再次联动。",
    impact: "会改变「事件配置」Tab 里出现的按钮项，保存前请确认事件是否仍符合预期。",
  },
  modalConfirm: {
    title: "弹窗确认",
    desc: "点击按钮后先弹出确认框，用户点确认才真正执行指令/事件，用于防误触。",
    usage: "关机、清空、重要模式切换等建议开启。",
    impact: "多一步确认，指令不会立即下发。",
  },
  pressRing: {
    title: "按压音效",
    desc: "按压按钮时是否播放按键音。",
    usage: "需要操作反馈时开启；嘈杂环境或静音展厅可关闭。",
    impact: "依赖终端是否支持对应音效资源。",
  },
  pressBackgroundType: {
    title: "按压背景类型",
    desc: "按压态用颜色还是图片，与常态背景独立配置，便于区分开/关态。",
    usage: "选颜色则配按压色；选图片则配按压背景图。建议与常态有明显差异。",
    impact: "仅按压态外观；不改变指令逻辑。",
  },
  pressBackgroundColor: {
    title: "按压颜色",
    desc: "按压背景类型为颜色时的填充色。",
    usage: "在按压背景类型选「颜色」后配置。",
    impact: "仅外观。",
  },
  pressBackgroundImage: {
    title: "按压背景图",
    desc: "按压态使用的背景图。",
    usage: "从素材库选择；尺寸建议与按钮宽高一致。",
    impact: "计入工程资源。",
  },
  btnIcon: {
    title: "图标选择",
    desc: "叠加在按钮文字旁/上的小图标，增强识别。",
    usage: "从可选图标列表中选择；无合适图标可不选。",
    impact: "仅外观。",
  },

  // —— 文本 ——
  allowChanges: {
    title: "允许变更",
    desc: "运行时是否允许文本内容被组件联动、外部逻辑等改写。",
    usage: "固定标题关掉；需要根据状态改字（如「已连接」）时打开。",
    impact: "关闭后外部/联动改字可能不生效。",
  },
  textScroll: {
    title: "文字滚动",
    desc: "文字相对组件区域过长时，是否以跑马灯方式滚动显示。",
    usage: "开启后配置滚动方向与速度；适合通知条、长标题。",
    impact: "开启后静态排版感减弱，注意速度不要过快。",
  },
  textScrollDir: {
    title: "滚动方向",
    desc: "文字滚动的方向（如水平左右）。",
    usage: "先开启「文字滚动」，再选方向。",
    impact: "仅展示效果。",
  },
  textScrollSpeed: {
    title: "滚动速度",
    desc: "文字滚动快慢。",
    usage: "按可读性调节：提示类可稍快，正文说明宜慢。",
    impact: "过快难以阅读；过慢信息吞吐低。",
  },

  // —— 媒体/其它组件 ——
  imageResource: {
    title: "图片资源",
    desc: "图片组件当前展示的素材。",
    usage: "从素材库选择；可与「图片尺寸适配」一起调裁切方式。",
    impact: "计入工程资源与导出包。",
  },
  backgroundSize: {
    title: "图片尺寸适配",
    desc: "图片或页面背景在框内的铺满/等比例缩放适配方式。",
    usage: "需要铺满无留白选铺满（可能变形）；需要完整显示选等比例（可能留边露底色）。缺省为铺满，兼容未配置的老工程。",
    impact: "只影响缩放显示，不改原图文件。",
  },
  rtspAdressGroups: {
    title: "RTSP 流地址",
    desc:
      "配置监控等 RTSP 取流地址（运行时仅播放第一组，请只填一条）。\n示例（海康）：\nrtsp://admin:12345@192.168.1.64:554/ch1/sub/av stream\n【rtsp://】协议\n【admin】账号\n【12345】密码\n【IP】摄像机地址\n【554】端口\n后续为通道/码流路径（主码流/子码流以厂商文档为准）",
    usage: "只填一条流地址；按摄像机品牌文档填写完整 URL；局域网需保证终端能访问该 IP。可先在 VLC 等工具验证地址再填入。",
    impact: "地址、账号或网络不通会导致黑屏/无法出流。历史工程若残留多组，终端仍只播第一组。",
  },
  videoNameGroups: {
    title: "视频文件名称",
    desc: "本平台暂不支持在项目中上传媒体素材。请在设备端本地配置平台上传对应文件，并确保此处填写的文件名称与设备上的实际文件名完全一致。",
    usage: "只填一条视频文件名（含扩展名）。需要讲解同步控设备时，到「事件配置」里按播放秒数绑定指令。",
    impact: "名称不一致时设备端无法找到对应文件，视频无法播放。",
  },
  musicNameGroups: {
    title: "音乐文件名称",
    desc: "本平台暂不支持在项目中上传媒体素材。请在设备端本地配置平台上传对应文件，并确保此处填写的文件名称与设备上的实际文件名完全一致。",
    usage: "只填一条音频文件名（含扩展名）；可开「循环播放」。常与按钮点击绑定配合做背景音。",
    impact: "名称不一致时设备端无法找到对应文件，音频无法播放。",
  },
  loopplay: {
    title: "循环播放",
    desc: "音频是否循环播放。",
    usage: "背景氛围音建议开启；一次性提示音关闭。",
    impact: "仅播放行为。",
  },
  url: {
    title: "网页地址",
    desc: "内嵌网页组件加载的 http/https 地址。",
    usage: "填写终端网络可达的地址；注意部分站点禁止 iframe 嵌套可能导致空白。",
    impact: "依赖终端网络与页面兼容性；跨域/证书问题会导致打不开。",
  },
  direction: {
    title: "滑块方向",
    desc: "滑块轨道为横向或纵向，决定拖动手势方向。",
    usage: "选定后调整组件宽高与轨道粗细，保证手指可操作区域足够。",
    impact: "影响交互手势；改方向后建议重新检查布局。",
  },
  circleColor: {
    title: "圆点颜色",
    desc: "滑块拖动圆点的颜色。",
    usage: "与轨道色、滑动色区分开，便于看清当前位置。",
    impact: "仅外观。",
  },
  circleSize: {
    title: "圆点大小",
    desc: "滑块圆点直径，影响可点性与视觉比重。",
    usage: "触屏场景宜稍大；过小不易拖准。",
    impact: "过小难操作；过大可能挡住刻度文字。",
  },
  trackColor: {
    title: "轨道颜色",
    desc: "滑块未激活（未滑过）轨道颜色。",
    usage: "选择与页面背景有一定对比的颜色。",
    impact: "仅外观。",
  },
  activeColor: {
    title: "滑动颜色",
    desc: "滑块已滑过区域的颜色，表示当前进度。",
    usage: "建议用品牌主色或高对比色。",
    impact: "仅外观。",
  },
  trackSize: {
    title: "轨道大小",
    desc: "滑块轨道粗细。",
    usage: "按设计调节；过细不易看清进度。",
    impact: "仅外观。",
  },
  documentNameGroups: {
    title: "文档文件名称",
    desc: "本平台暂不支持在项目中上传媒体素材。请在设备端本地配置平台上传对应文件，并确保此处填写的文件名称与设备上的实际文件名完全一致。",
    usage: "只填一条文档文件名（含扩展名），确认终端支持该格式。",
    impact: "名称不一致时设备端无法打开对应文档。",
  },
  city: {
    title: "城市",
    desc: "天气组件查询天气所对应的城市。",
    usage: "选择或填写正确城市名；需终端能访问天气数据服务。",
    impact: "城市错误则显示其它地区天气。",
  },
  imgStyle: {
    title: "天气样式",
    desc: "天气图标与版式风格（由后台样式列表提供）。",
    usage: "从下拉选择与整体 UI 匹配的样式。",
    impact: "仅外观。",
  },
  carouselPics: {
    title: "轮播图片列表",
    desc: "多图轮播依次展示的图片集合。",
    usage: "按展示顺序添加多张图，并设置「轮播间隔」；可配图片尺寸适配。",
    impact: "图片多、分辨率高会增大包体与内存占用。",
  },
  interval: {
    title: "轮播间隔",
    desc: "自动切换到下一张的间隔秒数。",
    usage: "宣传图可 3～8 秒；信息密集时可稍长。",
    impact: "过短闪烁刺眼；过长像静态图。",
  },
  carouselPlayMode: {
    title: "播放方式",
    desc: "原版与现网一致；其它模式可纯手动或自动+手动（悬停暂停需样式2）。",
    usage: "「仅手动」配合样式3 箭头；「自动+手动」配合样式2 圆点。",
    impact: "仅影响切换方式，不改变图片列表。",
  },
  carouselVisualStyle: {
    title: "轮播样式",
    desc: "样式1 封面流；样式2 左下圆点；样式3 左右箭头；原版与历史工程一致。",
    usage: "选非原版后可选横版/竖版。",
    impact: "客户端需同步解析 style.carouselExt。",
  },
  carouselLayoutDirection: {
    title: "布局方向",
    desc: "横版或竖版排列；箭头与封面流轴向随之变化。",
    usage: "竖版适合窄高组件。",
    impact: "仅在新样式下生效。",
  },
  musicControlIcon: {
    title: "音乐控件图标",
    desc: "替换默认音符 iconfont；不选则与历史工程一致。",
    usage: "从素材库选 PNG/SVG 等，建议正方形透明底。",
    impact: "写入 style.musicExt，客户端需解析 controlIconSrc。",
  },
  lightBackground: {
    title: "状态灯背景",
    desc: "状态灯在不同监测/回码结果下展示的背景（颜色或图片），用于表达在线、离线、开、关等。",
    usage: "先配「网络状态」或「开关状态」逻辑，再为各结果态设置对应外观，保证用户一眼能区分。",
    impact: "与网络状态/开关状态结果联动；未配结果态时观感不完整。",
  },
  displayMode: {
    title: "可视化显示模式",
    desc: "可视化组件列表项如何展示：缩略图+文件名 / 仅缩略图 / 仅文件名等。",
    usage: "按触控屏信息密度选择；同时在「网络配置」填服务 IP、播放器 IP、素材根目录。",
    impact: "影响终端如何拉取与展示可视化内容；网络参数错误则列表为空。",
  },

  // —— 网络配置 ——
  directives: {
    title: "指令（按下/主指令）",
    desc: "按钮等触发时下发的主指令列表，通常对应按下或主动作。",
    usage: "点「添加指令」从指令库勾选，可多条按顺序下发；先确认目标设备 IP/端口正确。",
    impact: "直接决定设备收到的控制内容；错指令可能导致误动作，重要操作建议加弹窗确认。",
  },
  directives2: {
    title: "指令2（再次/抬起等）",
    desc: "第二组指令，常用于再次按压、抬起或开关成对动作。单击模式下入口可能隐藏。",
    usage: "与第一组配合：例如指令1=开、指令2=关；保存前用预览验证两次按压是否符合预期。",
    impact: "影响二次触发时的下发内容。",
  },
  ipAdress: {
    title: "IP 地址",
    desc: "组件网络通信目标设备/服务的 IPv4 地址。",
    usage: "填写局域网可达地址；保存前注意格式校验。环境组件、滑块、矩阵、生态组、可视化等均依赖此项。",
    impact: "错误则指令或状态监测无法连通。",
  },
  port: {
    title: "端口",
    desc: "目标服务端口号，与 IP 成对使用。",
    usage: "按设备协议或中控服务文档填写。",
    impact: "端口不通表现为超时、无响应。",
  },
  input: {
    title: "输入位",
    desc: "矩阵「输入」按键对应的输入位标识，供执行键规则中的 X 引用。",
    usage: "按现场矩阵方案编号填写，并与「执行」键规则一致。",
    impact: "填错会导致切换到错误输入源。",
  },
  output: {
    title: "输出位",
    desc: "矩阵「输出」按键对应的输出位标识，供执行键规则中的 Y 引用。",
    usage: "与输入位、规则引擎统一规划编号。",
    impact: "填错会导致输出通道错误。",
  },
  rulesEngine: {
    title: "规则引擎",
    desc: "矩阵「执行」键的规则表达式。界面提示：X=输入位，Y=输出位。",
    usage: "按提示编写规则，并配置执行端 IP/端口；先配好输入/输出键再写规则。",
    impact: "决定按下执行键时如何组合输入与输出。",
  },
  sliderList: {
    title: "滑块单元",
    desc: "滑块可切换的「单位 + 代码」列表，用于同一滑块下发不同量纲或协议字段。",
    usage: "至少配置 2 组；每组填写单位名称与对应代码，保存前通过校验。拖动滑块时按当前单元组装下发值。",
    impact: "单元或代码错误会导致设备收到无法识别的数值。",
  },
  bfqIpAdress: {
    title: "播放器 IP",
    desc: "可视化场景中播放器或相关服务的 IP。",
    usage: "按现场部署填写，需与服务端、素材服务网络互通。",
    impact: "不通则无法取流/取列表。",
  },
  bfqPort: {
    title: "播放器端口",
    desc: "可视化播放器服务端口。",
    usage: "与播放器 IP 成对配置。",
    impact: "连通性。",
  },
  rootDirectory: {
    title: "根目录",
    desc: "可视化素材在服务上的根路径。",
    usage: "填写服务真实目录路径，注意大小写与斜杠；改目录后需确认文件仍在其下。",
    impact: "路径错误会导致资源找不到、列表为空。",
  },

  // —— 事件 ——
  "event.mutuallyEvent": {
    title: "互斥事件",
    desc: "多个按钮组成互斥组：按下其中一个时，其它已绑定按钮进入指定状态（如释放），类似单选。",
    usage: "点按钮打开弹窗，勾选同组其它按钮及目标状态；常用于模式切换（只允许一种模式按下）。",
    impact: "避免多按钮同时保持按下；与联动事件不同，侧重「互斥释放」。",
  },
  "event.jumpEvent": {
    title: "跳转事件",
    desc: "点击后跳转到工程内其它页面。需开启「是否单击按钮」后才出现该入口。",
    usage: "在弹窗中选择目标页；可与网络指令同时配置（先发指令再跳转，或按产品实际顺序）。",
    impact: "改变当前显示页面；目标页若懒加载，首次进入可能有短暂加载。",
  },
  "event.pressEvent": {
    title: "联动事件",
    desc: "按下本按钮时，联动改变其它按钮的按压/释放状态，用于一键场景。",
    usage: "弹窗中勾选被联动按钮及目标状态；可与互斥、指令一起用。",
    impact: "批量改变界面状态，不替代网络指令本身（指令仍在网络配置里）。",
  },
  "event.pressAgainEvent": {
    title: "再次按压联动",
    desc: "再次按压（抬起成对动作）时的状态联动。未开启「单击按钮」时可用。",
    usage: "配置「第二次动作」时其它按钮应处的状态，常与开关逻辑、指令2配合。",
    impact: "决定关/松手时的界面恢复效果。",
  },
  "event.videoBind": {
    title: "视频时间点指令",
    desc: "视频播放到指定秒数时自动下发指令，用于讲解/演示与设备同步。",
    usage: "在事件里点「添加」增加时间点，填写秒数并「添加指令」；可配多条时间轴。",
    impact: "秒数不准会感觉不同步；指令错误仍会影响设备。",
  },
  "event.clickBind": {
    title: "点击绑定",
    desc: "单击模式下，把本按钮的点击映射到其它组件的某个事件（如视频播放、轮播翻页、音乐播放、打开网页）。",
    usage: "开启「是否单击按钮」→ 事件配置里先选组件再选事件；被绑组件需已在本页存在。",
    impact: "点击本按钮等同触发目标组件事件，减少重复控件。",
  },

  // —— 状态灯 / 主动探测 / 被动监测 ——
  deviceName: {
    title: "设备名称",
    desc: "被监测设备的显示名称，便于在多设备列表中区分。",
    usage: "用现场习惯命名（如「主机房交换机」）；一般不参与协议字段。",
    impact: "主要用于识别与运维可读性。",
  },
  ip: {
    title: "监测 IP",
    desc: "主动探测（Ping 等）或被动监测所针对的设备 IP。",
    usage: "填写真实可达地址；与端口、探测参数一起保存后预览验证灯态。",
    impact: "错误则灯态无法反映真实设备。",
  },
  logRecordStatus: {
    title: "状态 log",
    desc: "是否记录状态变化日志，便于排查误报/漏报。",
    usage: "联调阶段建议开启，稳定后可按需关闭。",
    impact: "可能增加日志量。",
  },
  bindDirectives: {
    title: "绑定指令",
    desc: "状态监测相关的指令绑定（如探测附带指令、开关态关联指令）。",
    usage: "点添加从指令库选择；与成功/失败或回码表现一起配置。",
    impact: "影响监测过程是否附带控制，或回码如何对应。",
  },
  devices: {
    title: "设备列表",
    desc: "开关状态下可配置多台设备，各自有绑定指令、探测与回码表现。",
    usage: "点「添加设备」，逐台填写；展开折叠面板编辑。",
    impact: "多设备时注意性能与回码冲突，避免多台规则互相覆盖灯态。",
  },

  // —— 分区说明 ——
  "section.style": {
    title: "组件属性",
    desc: "当前选中组件的外观与业务相关配置，随组件类型变化（按钮、视频、状态灯等字段不同）。",
    usage: "逐项填写；标签前带橘黄小圆点的可点击查看该功能的详细说明（用途、用法、影响）。",
    impact: "决定显示效果与部分运行行为；改完建议预览验证。",
  },
  "section.componentLinks": {
    title: "组件联动",
    desc: "监听其它组件的事件，触发时修改本组件若干属性（如透明度、颜色、图片）。仅在预览/运行生效，编辑态不一定立刻表现。",
    usage: "选择监听的组件 → 选择事件 → 配置要改的属性与目标值 → 可设过渡时间（毫秒）。用于显隐、换色、换图等动画。",
    impact: "不替代网络指令下发；复杂联动建议命名清晰并预览逐步验证。",
  },
  "section.network": {
    title: "网络配置",
    desc: "组件与外部设备/服务通信的指令与连接参数（IP、端口、指令、矩阵位、滑块单元等）。",
    usage: "按组件类型填写；带橘黄点的项可点开看说明。改完务必预览或联调设备。",
    impact: "直接影响控制是否成功、控制的是哪台设备。",
  },
  "section.events": {
    title: "事件配置",
    desc: "点击后的页面跳转、按钮互斥/联动、视频时间点指令、单击绑定等交互。",
    usage: "先确认按钮是否「单击」模式，再配置对应事件按钮；每项前的橘黄点有详细说明。",
    impact: "决定用户操作后的页面与状态变化，可与网络指令同时存在。",
  },
  "section.interactionPackage": {
    title: "交互包",
    desc: "工程级协议与指令编排、按钮状态规则的集中入口（工具栏「交互包」），与单个组件属性面板独立。",
    usage: "先添加协议（UDP/TCP/HTTP）→ 配指令集或按钮状态规则 → 与工程一并保存。",
    impact: "影响外部消息驱动界面/指令的统一行为。",
  },

  // —— 右侧 Tab ——
  "tab.networkConfig": {
    title: "网络配置",
    desc: "配置组件与外部设备通信所需的 IP、端口、指令、矩阵位或滑块单元等。",
    usage: "选中带网络能力的组件后出现本 Tab；按类型填写，并通过「添加指令」绑定下发内容。字段前橘黄点可查看细项说明。",
    impact: "直接决定点击/滑动等操作能否正确控制设备。",
  },
  "tab.events": {
    title: "事件配置",
    desc: "配置点击后的页面跳转、按钮互斥/联动，或视频按秒触发指令等。",
    usage:
      "按钮：可配互斥、联动；开启「单击按钮」后可配跳转与点击绑定；未开启时可配再次按压联动。\n视频：按播放秒数绑定指令。\n点橘黄点可看每个事件的详细用法。",
    impact: "决定用户操作后的页面切换与状态变化，可与网络指令同时生效。",
  },
  "tab.activeDetectionConfig": {
    title: "网络状态",
    desc: "状态灯通过 Ping 等方式主动探测设备在线/离线，并切换灯的表现（颜色/图片）。",
    usage: "填写设备名、IP、端口与探测相关参数；配置探测成功/失败时的显示。联调时建议打开状态 log。",
    impact: "用于运维可视监测，不替代按钮发指令；网络不通时灯会显示失败态。",
  },
  "tab.passiveMonitorConfig": {
    title: "开关状态",
    desc: "状态灯根据设备回码、绑定指令结果，被动反映开关类状态，支持多设备。",
    usage: "添加设备 → 配绑定指令、探测时间与回码表现 → 再在组件属性里配各结果态背景。",
    impact: "用于把设备真实开关反馈到界面；回码规则配错会出现误亮/不亮。",
  },
};

export function getAttrHelp(key, component) {
  if (!key) {
    return null;
  }
  if (component) {
    const specific = ATTR_HELP_MAP[`${component}.${key}`];
    if (specific) {
      return specific;
    }
  }
  return ATTR_HELP_MAP[key] || null;
}
